import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material'
import {
  ArrowBack,
  Home,
  Folder,
  Assignment
} from '@mui/icons-material'
import api from '../services/api'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import Checklist from '../components/mrd/deficiency/checklist'

export const DeficiencyPage = () => {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)
  const [checklist, setChecklist] = useState([])
  const [fileInfo, setFileInfo] = useState(null)

  useEffect(() => {
    if (fileId) {
      loadFileChecklist()
    } else {
      // Redirect to MRD Files if no fileId provided
      navigate('/hospital/mrd/mrd-files')
    }
  }, [fileId, navigate])

  const loadFileChecklist = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/mrd/deficiency/${fileId}`, {
        params: { includeCompleted: true }
      })
      const data = response.data.data
      setChecklist(data.items || [])
      setFileInfo(data.mrdFile)
    } catch (error) {
      enqueueSnackbar('Failed to load checklist', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }



  const handleUpdateItem = async (itemId, updates) => {
    try {
      await api.put(`/mrd/deficiency/item/${itemId}`, updates)
      enqueueSnackbar('Checklist item updated', { variant: 'success' })
      loadFileChecklist()
    } catch (error) {
      enqueueSnackbar('Failed to update item', { variant: 'error' })
    }
  }

  const handleAddDeficiency = async (data) => {
    try {
      await api.post(`/mrd/deficiency/${fileId}`, data)
      enqueueSnackbar('Deficiency added to checklist', { variant: 'success' })
      loadFileChecklist()
    } catch (error) {
      enqueueSnackbar('Failed to add deficiency', { variant: 'error' })
    }
  }

  const handleEscalate = async (itemId, escalateTo, reason) => {
    try {
      await api.post(`/api/v1/mrd/deficiency/escalate/${itemId}`, {
        escalateTo,
        reason
      })
      enqueueSnackbar('Deficiency escalated', { variant: 'warning' })
      loadFileChecklist()
    } catch (error) {
      enqueueSnackbar('Failed to escalate', { variant: 'error' })
    }
  }

  // Only render if fileId is provided
  if (!fileId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          No file ID provided. Redirecting to MRD Files...
        </Alert>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/dashboard')}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/mrd-files')}
          >
            <Folder sx={{ mr: 0.5 }} fontSize="small" />
            MRD Files
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            color="text.primary"
          >
            <Assignment sx={{ mr: 0.5 }} fontSize="small" />
            Deficiency Checklist
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Deficiency Checklist
              </Typography>
              {fileInfo && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                  <Chip
                    label={`File: ${fileInfo.fileNumber}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="body1" color="text.secondary">
                    Patient: <strong>{fileInfo.patientName}</strong>
                  </Typography>
                  {fileInfo.department && (
                    <Chip
                      label={fileInfo.department.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/hospital/mrd/mrd-files')}
              size="large"
            >
              Back to Files
            </Button>
          </Box>
        </Box>

        {/* Checklist Component */}
        <Checklist
          fileId={fileId}
          checklist={checklist}
          fileInfo={fileInfo}
          loading={loading}
          onUpdateItem={handleUpdateItem}
          onAddDeficiency={handleAddDeficiency}
          onEscalate={handleEscalate}
          onBack={() => navigate('/hospital/mrd/mrd-files')}
        />
      </Container>
    </Box>
  )
}

